import React, { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { smoothScrollToElement } from "../utils/smoothScroll";
import { useForm, ValidationError } from '@formspree/react';
import {
  FaHandHoldingMedical,
  FaRegCircleCheck,
  FaPhoneVolume,
  FaPhone,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaTiktok,
  FaAt,
  FaEnvelope,
  FaPaperPlane,
  FaLocationDot,
  FaAddressCard,
  FaClock,
  FaMessage
} from "react-icons/fa6";
import { FiAlertCircle } from "react-icons/fi";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import esLabels from "react-phone-number-input/locale/es.json";
import flags from 'react-phone-number-input/flags'
import { contactInfo, NAME } from '../constants';
import GuatemalaFlag from "../assets/flags/flaggt.svg";
import MexicoFlag from "../assets/flags/flagmx.svg";
import UsFlag from "../assets/flags/flagus.svg";
import Globe from "../assets/flags/globe.svg";
import "react-phone-number-input/style.css";
import './Contact.css';

const FORM_ID = process.env.NEXT_PUBLIC_FORM || process.env.REACT_APP_CONTACT_FORM as string;
function flagEmoji(iso2: string) {
  return iso2
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + (c.charCodeAt(0) - 65)));
}

const labelsWithFlags: Record<string, string> = Object.fromEntries(
  Object.entries(esLabels).map(([iso2, name]) => [iso2, `${flagEmoji(iso2)} ${name}`])
);
const Contact: React.FC = () => {
  const [state, handleSubmit] = useForm(FORM_ID);
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;

    setTimeout(() => {
      smoothScrollToElement(el, { offset: 75, duration: 1500 });
    }, 0);
  }, [hash]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    preferredTime: '',
    serviceType: ''
  });

  const [phone, setPhone] = useState<string>("");
  const valid = !phone || isValidPhoneNumber(phone);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    if (!isValidPhoneNumber(phone)) return;
    if ((fd.get('website') as string)?.trim()) return;

    formData.phone = phone;
    try {
      await handleSubmit(e);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        preferredTime: '',
        serviceType: ''
      });

    } catch (error) {
      if (!state?.errors?.kind) {
        form.reset();
      }
    } finally {
      setTimeout(() => console.log(state), 5000);
    }
  }

  const whatsappMessage = encodeURIComponent(
    `Hola ${NAME}, me gustaría agendar una consulta. Mi nombre es ${formData.name || '[Nombre]'} y estoy interesado/a en ${formData.serviceType || 'información sobre sus servicios'}.`
  );

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content text-center">
            <h1>¿Cómo prefieres contactarme?</h1>
            <p className="contact-hero-subtitle">
              Estoy aquí para ayudarte. No dudes en contactarme para agendar
              tu consulta o resolver cualquier duda que tengas.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header text-center">
            <h2>Escoge el medio que prefieras</h2>
            <p className="section-subtitle">
              Elige la forma de contacto que más te convenga
            </p>
          </div>

          <div className="contact-methods">
            <div className="contact-method">
              <div className="method-icon whatsapp-bg">
                <FaWhatsapp size={32} />
              </div>
              <h3>WhatsApp</h3>
              <p>Respuesta rápida y directa para agendar citas</p>
              <a
                href={`https://wa.me/${contactInfo.completePhone}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <FaPaperPlane className="icon" size={25} />
                ¡Escríbeme!
              </a>
            </div>

            <div className="contact-method">
              <div className="method-icon phone-bg">
                <FaPhoneVolume size={32} />
              </div>
              <h3>Teléfono</h3>
              <p>Llámame directamente para una atención personalizada</p>
              <a href={`tel:+502${contactInfo.phone}`} className="btn btn-primary">
                <FaPhone className="icon" />
                ¡Llámame!
              </a>
            </div>

            <div className="contact-method">
              <div className="method-icon email-bg">
                <FaAt size={32} />
              </div>
              <h3>Email</h3>
              <p>Envíame un correo detallado con tus consultas</p>
              <a href={`mailto:${contactInfo.email}`} className="btn btn-secondary">
                <FaEnvelope className="icon" />
                ¡Envíame un correo!
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section section-dark" id="contact-form">
        <div className="container">
          <div className="contact-content">
            <div className="contact-form-section">
              <div className="form-header">
                <h2>Construyamos tu bienestar, a tu ritmo</h2>
                <p>
                  Cuéntame qué necesitas y te responderé a la brevedad.
                </p>
              </div>

              {state.succeeded && (
                <div className="alert alert-success">
                  <FaRegCircleCheck size={20} />
                  <span>¡Gracias! Recibí tu mensaje. Te escribiré pronto</span>
                </div>
              )}

              {state.errors?.kind && (
                <div className="alert alert-error">
                  <FiAlertCircle size={20} />
                  <span>Hubo un error al enviar el mensaje. Inténtalo nuevamente.</span>
                </div>
              )}

              <form onSubmit={onSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    <FaAddressCard size={18} />
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Emily Gamarro"
                  />
                  <ValidationError prefix="Name" field="name" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <FaAt size={18} />
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="tucorreo@ejemplo.com"
                  />
                  <ValidationError prefix="Email" field="email" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    <FaPhone size={18} />
                    Teléfono
                  </label>
                  <PhoneInput
                    value={phone}
                    onChange={(v) => setPhone(v || "")}
                    defaultCountry="GT"
                    flags={flags}
                    countries={["GT", "MX", "US"]}
                    international
                    placeholder="+502 XXXX XXXX"
                    labels={labelsWithFlags}
                    className="form-input"
                  />
                  {!valid && (
                    <p className="wrong-number">Número inválido. </p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="serviceType" className="form-label">
                    <FaHandHoldingMedical size={18} />
                    Tipo de servicio
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="" disabled hidden> Dime en que servicio estas interesado</option>
                    <option value="Terapia Individual">Psicoterapia Individual</option>
                    <option value="Terapia de Pareja">Terapia Conductual Integrativa de Pareja</option>
                    <option value="Terapia Familiar">Psicoterapia en Cuidado Paliativos</option>
                    <option value="Evaluación Psicológica">Terapia Centrada en el Trauma</option>
                    <option value="Terapia Online">Taller</option>
                    <option value="Coaching Psicológico">Servicios Empresariales</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <FaMessage size={18} />
                    Mensaje: (opcional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows={4}
                    maxLength={265}
                    placeholder="¿Qué te gustaría trabajar o preguntar? Cuéntame los detalles."
                  />
                  <ValidationError prefix="Message" field="message" errors={state.errors} />
                </div>
                <div className="form-group">
                  <label htmlFor="preferredTime" className="form-label">
                    <FaClock size={18} />
                    Horario preferido
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    value={formData.preferredTime}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">¿Cuál es tu horario preferido?</option>
                    <option value="Mañana (9:00-12:00)">Mañana (9:00-12:00)</option>
                    <option value="Mediodía (12:00-15:00)">Mediodía (12:00-15:00)</option>
                    <option value="Tarde (15:00-18:00)">Tarde (15:00-18:00)</option>
                    <option value="Noche (18:00-21:00)">Noche (18:00-21:00)</option>
                    <option value="Sábado mañana">Sábado Mañana</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary form-submit"
                  disabled={state.submitting || !valid || !phone}
                >
                  {state.submitting ? (
                    <>Enviando...</>
                  ) : (
                    <>
                      <FaPaperPlane className="icon" />
                      Enviar mensaje
                    </>
                  )}
                </button>
                <p className='data-protection'>Trataré tus datos con confidencialidad. Solo los usaré para contactarte.</p>
              </form>
            </div>

            <div className="contact-info-section">
              <div className="contact-info-card">
                <h3>Información de Contacto</h3>
                <div className="contact-details">
                  <div className="contact-detail">
                    <FaLocationDot className="detail-icon" />
                    <div>
                      <strong>Dirección</strong>
                      <p>Ciudad de Guatemala, Guatemala</p>
                    </div>
                  </div>
                  <div className="contact-detail">
                    <FaPhone
                      className="detail-icon" />
                    <div>
                      <strong>Teléfono</strong>
                      <p>+502 {contactInfo.phone}</p>
                    </div>
                  </div>
                  <div className="contact-detail">
                    <FaAt className="detail-icon" />
                    <div>
                      <strong>Email</strong>
                      <p>{contactInfo.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="schedule-card">
                <h3>Horarios de Atención</h3>
                <div className="schedule-list">
                  <div className="contact-schedule-item">
                    <strong>Lunes - Viernes</strong>
                    <span>9:00 - 21:00</span>
                  </div>
                  <div className="contact-schedule-item">
                    <strong>Sábados</strong>
                    <span>9:00 - 12:00</span>
                  </div>
                  <div className="contact-schedule-item">
                    <strong>Domingos</strong>
                    <span>No Disponible</span>
                  </div>
                </div>
              </div>

              <div className="emergency-card">
                <h3>¿Necesitas ayuda urgente?</h3>
                <p>
                  Si estás en crisis o necesitas ayuda inmediata,
                  contacta con los servicios de emergencia:
                </p>
                <div className="emergency-numbers">
                  <details open className="emergency-item panel">
                    <summary>
                      <span>
                        <img src={GuatemalaFlag} alt="Bandera de Guatemala" className="icon" />
                      </span> <strong>Guatemala</strong>
                    </summary>
                    <ul>
                      <li><strong>Bomberos/Ambulancia:</strong> <a href="tel:+502122">122</a> o <a href="tel:+502123">123</a></li>
                      <li><strong>Salud mental “Alerta Joven”:</strong> <a href="tel:+5021503">1503</a></li>
                    </ul>
                  </details>

                  <details className="emergency-item panel">
                    <summary>
                      <span>
                        <img src={MexicoFlag} alt="Bandera de Mexico" className="icon" />
                      </span> <strong>México</strong>
                    </summary>
                    <ul>
                      <li><strong>Emergencias:</strong> <a href="tel:+52911">911</a></li>
                      <li><strong>Línea de la Vida:</strong> <a href="tel:+528009112000">800 911 2000</a></li>
                      <li><strong>Consejo Ciudadano:</strong> <a href="tel:+525555335533">55 5533 5533</a> </li>
                    </ul>
                  </details>

                  <details className="emergency-item panel">
                    <summary>
                      <span>
                        <img src={UsFlag} alt="Bandera de Estados Unidos" className="icon" />
                      </span> <strong>Estados Unidos</strong>
                    </summary>
                    <ul>
                      <li><strong>Emergencias:</strong> <a href="tel:911">911</a></li>
                      <li><strong>988 Suicide &amp; Crisis Lifeline:</strong> <a href="https://988lifeline.org/chat/">Chat</a></li>
                    </ul>
                  </details>

                  <details className="emergency-item panel">
                    <summary>
                      <span>
                        <img src={Globe} alt="Internacional" className="icon" />
                      </span> <strong>Internacional</strong>
                    </summary>
                    <ul>
                      <li><a href="https://befrienders.org/" rel="noopener">Befrienders Worldwide</a></li>
                      <li><a href="https://findahelpline.com/" rel="noopener">Find a Helpline</a></li>
                    </ul>
                  </details>

                </div>

              </div>

              <div className="social-card">
                <h3>Sígueme en Redes Sociales</h3>
                <div className="social-links">
                  <a href="https://instagram.com/emgamarro.psicologia" className="social-link" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <FaInstagram size={20} />
                  </a>
                  <a href="https://www.facebook.com/share/1Cd4JkWwuR/?mibextid=wwXIfr" className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <FaFacebook size={20} />
                  </a>
                  <a href="https://www.tiktok.com/@113341344_" className="social-link" aria-label="TikTok" target="_blank" rel="noopener noreferrer">
                    <FaTiktok size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;